
import { useState, useCallback, useEffect } from 'react';
import { reverseGeocode, supabase } from '@/integrations/supabase/client';
import { detectUserTimezone, getTimezoneDisplayWithOffset } from '@/utils/timezone-detection';

export const usePractitionersLocation = () => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [userAddress, setUserAddress] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string | null>(null);
  const [timezoneDisplay, setTimezoneDisplay] = useState<string | null>(null);
  const [isApproximateLocation, setIsApproximateLocation] = useState(false);

  // Initialize timezone on component mount
  useEffect(() => {
    const timezone = detectUserTimezone();
    setUserTimezone(timezone);
    setTimezoneDisplay(getTimezoneDisplayWithOffset(timezone));
  }, []);

  // Check if geolocation is policy-blocked (iframe restrictions)
  const isGeolocationPolicyBlocked = useCallback(() => {
    try {
      // Check permissions policy if available
      if ('permissionsPolicy' in document && document.permissionsPolicy) {
        return !(document.permissionsPolicy as any).allowsFeature('geolocation');
      }
      return false;
    } catch (error) {
      console.log("Cannot check permissions policy:", error);
      return false;
    }
  }, []);

  // Fallback to IP-based approximate location
  const getApproximateLocation = useCallback(async () => {
    try {
      console.log('Attempting IP-based approximate location...');
      const { data, error } = await supabase.functions.invoke('get-approx-location');
      
      if (error) {
        console.error('IP location service error:', error);
        throw error;
      }

      if (data?.success && data?.data) {
        console.log('IP location resolved:', data.data);
        setUserLocation({ lat: data.data.lat, lng: data.data.lng });
        setCity(data.data.city);
        setState(data.data.state);
        setCountry(data.data.country);
        setUserAddress(data.data.formatted_address);
        setIsApproximateLocation(true);
        setLocationError(null);
        return true;
      }
      
      throw new Error('No location data returned');
    } catch (error) {
      console.error('Approximate location failed:', error);
      return false;
    }
  }, []);

  // Enhanced location request with policy checking
  const requestLocationAccess = useCallback(async () => {
    setIsGettingLocation(true);
    setLocationError(null);
    setIsApproximateLocation(false);
    
    // Detect timezone when requesting location
    const timezone = detectUserTimezone();
    setUserTimezone(timezone);
    setTimezoneDisplay(getTimezoneDisplayWithOffset(timezone));
    
    // Check if we're in an iframe and if geolocation is policy-blocked
    const isInIframe = window !== window.top;
    const isPolicyBlocked = isGeolocationPolicyBlocked();
    
    console.log('Environment check:', { 
      isInIframe, 
      isPolicyBlocked,
      isSecure: location.protocol === 'https:' || location.hostname === 'localhost',
      hostname: location.hostname 
    });
    
    // If geolocation is policy-blocked, try IP fallback first
    if (isPolicyBlocked) {
      console.log('Geolocation blocked by policy, trying IP fallback...');
      const fallbackSuccess = await getApproximateLocation();
      if (fallbackSuccess) {
        setLocationError("Using approximate location from your IP address. For precise location, enable geolocation in site settings.");
      } else {
        setLocationError("Location services are disabled. Please use manual address selection instead.");
      }
      setIsGettingLocation(false);
      return;
    }
    
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      setLocationError('GPS not supported by this browser. Please use manual address selection.');
      setIsGettingLocation(false);
      return;
    }

    // Check permissions first if available
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        setPermissionStatus(result.state);
        if (result.state === 'denied') {
          console.log('Geolocation permission denied, trying IP fallback...');
          const fallbackSuccess = await getApproximateLocation();
          if (fallbackSuccess) {
            setLocationError("Location permission denied. Using approximate location from your IP address.");
          } else {
            setLocationError("Location access has been denied. Please enable location services or use manual address selection.");
          }
          setIsGettingLocation(false);
          return;
        }
      } catch (error) {
        console.log("Permission query not supported:", error);
      }
    }
    
    const timeout = setTimeout(() => {
      setIsGettingLocation(false);
      setLocationError("Location request timed out. Please try manual address selection instead.");
    }, 10000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeout);
        console.log("Location obtained with accuracy:", position.coords.accuracy, "meters");
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setIsGettingLocation(false);
        setLocationError(null);
      },
      async (error) => {
        clearTimeout(timeout);
        console.error("Geolocation error:", error);
        
        // Try IP fallback for permission denied or policy issues
        if (error.code === error.PERMISSION_DENIED) {
          console.log('Geolocation failed with PERMISSION_DENIED, trying IP fallback...');
          const fallbackSuccess = await getApproximateLocation();
          if (fallbackSuccess) {
            setLocationError("Precise location unavailable. Using approximate location from your IP address.");
            setIsGettingLocation(false);
            return;
          }
        }
        
        let errorMessage = "Location access failed. ";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += "Please enable location services or use manual address selection.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location unavailable. Please use manual address selection.";
            break;
          case error.TIMEOUT:
            errorMessage += "Request timed out. Please use manual address selection.";
            break;
          default:
            errorMessage += "Please use manual address selection.";
            break;
        }
        
        setLocationError(errorMessage);
        setIsGettingLocation(false);
      },
      { 
        timeout: 8000,
        enableHighAccuracy: true,
        maximumAge: 300000
      }
    );
  }, [isGeolocationPolicyBlocked, getApproximateLocation]);

  // Manual address selection handler
  const setManualLocation = useCallback((lat: number, lng: number, address: string) => {
    setUserLocation({ lat, lng });
    setUserAddress(address);
    setLocationError(null);
    setIsApproximateLocation(true);
    
    // Parse address components if possible
    const parts = address.split(', ');
    if (parts.length >= 3) {
      setCity(parts[0] || null);
      setState(parts[parts.length - 2] || null);
      setCountry(parts[parts.length - 1] || null);
    }
  }, []);


  // Enhanced address fetching using Supabase edge function
  useEffect(() => {
    const fetchAddress = async () => {
      if (!userLocation) return;
      
      setIsLoadingAddress(true);
      
      try {
        console.log("Fetching address for coordinates:", userLocation);
        const { data, error } = await reverseGeocode(userLocation.lat, userLocation.lng);
        
        if (error) {
          console.error("Reverse geocoding error:", error);
          throw error;
        }

        if (data) {
          console.log("Received geocoding data:", data);
          setCity(data.city || null);
          setState(data.state || null);
          setCountry(data.country || null);
          setUserAddress(data.formatted_address || null);
          setLocationError(null);
          console.log("Address resolved successfully:", data.formatted_address);
        } else {
          console.warn("No address data received");
          throw new Error("No address data received");
        }
      } catch (error) {
        console.error("Address fetch failed:", error);
        setLocationError(`Could not determine address from location: ${error.message || 'Unknown error'}`);
        // Set fallback location data
        setCity("Unknown City");
        setState("");
        setCountry("");
        setUserAddress(`Location: ${userLocation.lat.toFixed(4)}, ${userLocation.lng.toFixed(4)}`);
      } finally {
        setIsLoadingAddress(false);
      }
    };

    if (userLocation && !isLoadingAddress) {
      fetchAddress();
    }
  }, [userLocation]);

  return {
    userLocation,
    userAddress,
    locationError,
    isGettingLocation,
    isLoadingAddress,
    city,
    state,
    country,
    permissionStatus,
    userTimezone,
    timezoneDisplay,
    isApproximateLocation,
    requestLocationAccess,
    setManualLocation,
    isGeolocationPolicyBlocked,
    getApproximateLocation
  };
};
